import styled from '@emotion/styled';
import Pencil from 'ui/images/icons/pencil.svg';
import { SubTitleLabel } from 'ui/components/elements/ColorLabel/SubTitleLabel';
import { ColorLabel } from 'ui/components/elements/ColorLabel/ColorLabel';
import { mqMin, mqUser } from 'utils/responsive';
import { AGENT_TYPES } from 'domain/entities/Agent/Agent';

const ContentWrapper = styled.div`
    width: 100%;
    height: auto;
    background: #f8f9fd;
    box-shadow: 0px 8px 24px rgba(0, 0, 0, 0.12);
    border-radius: 16px;
    min-width: 320px;
    overflow: hidden;

    ${mqMin('NARROW')} {
        max-width: 1000px;
    }
`;

const HeaderSection = styled.div`
    position: relative;
    width: 100%;
    padding: 40px 30px 20px 30px;
    display: flex;
    gap: 24px;
    align-items: flex-start;

    ${mqUser('SP')} {
        flex-direction: column;
        padding: 30px 20px 50px 20px;
        gap: 20px;
    }
`;

const CompanyImageWrapper = styled.div`
    flex-shrink: 0;
    width: 120px;
    height: 120px;
    background-color: var(--white);
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.1);
    border: 3px solid #d3d3d3;
    overflow: hidden;
    position: relative;
    z-index: 1;
    margin-top: 0;

    ${mqUser('SP')} {
        width: 100px;
        height: 100px;
    }
`;

const CompanyImage = styled.img`
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
`;

const CompanyInfo = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 8px;
    position: relative;
    z-index: 1;
`;

const CompanyName = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: var(--font-xxx-large);
    font-weight: 900;
    color: var(--dark-blue);
    line-height: 1.2;
    letter-spacing: -0.5px;
    margin-bottom: 5px;

    ${mqUser('SP')} {
        font-size: var(--font-x-large);
    }
`;

const CompanyLocation = styled.div`
    font-size: var(--font-large);
    font-weight: 500;
    color: var(--dark-blue);
    line-height: 1.6;
    opacity: 0.9;
`;

const ContentSection = styled.div`
    padding: 30px;
    display: flex;
    flex-direction: column;
    gap: 20px;

    ${mqUser('SP')} {
        padding: 20px;
    }
`;

const Item = styled.div`
    display: flex;
    gap: 12px;
    flex-direction: column;
    padding: 16px 20px;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
`;

const Title = styled.div`
    font-size: var(--font-medium);
    font-weight: 700;
    color: var(--dark-blue);
    letter-spacing: 0.3px;
`;

const TextArea = styled.div`
    font-size: var(--font-large);
    padding: 16px 20px;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
    color: var(--dark-blue);
    line-height: 1.8;

    p {
        line-height: 1.8;
        margin: 0;
    }
`;

const GrayLabel = styled.div`
    border-radius: 4px;
    background-color: #f3f3f3;
    align-items: center;
    font-size: var(--font-medium);
    color: #222222;
    font-weight: 500;
    padding: 0.2rem 0.9rem;
`;

const IndustryLabelContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    align-items: center;
`;

const AreaLabelContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    align-items: center;
`;

const Wrapper = styled.div`
    width: 100%;
`;

const EditButton = styled.button`
    background-color: var(--dark-blue);
    color: var(--white);
    border: none;
    border-radius: 25px;
    padding: 10px 20px;
    width: fit-content;
    cursor: pointer;
    font-size: var(--font-large);
    font-weight: var(--font-bold);
    margin-bottom: 10px;
    display: flex;
    align-items: center;
    gap: 5px;
    &:hover {
        opacity: 0.9;
    }
    &:active {
        transform: scale(0.98);
    }

    img {
        width: 15px;
    }
`;

const SubtitleContainer = styled.div`
    display: flex;
    gap: 10px;
    align-items: center;
`;

const Subtitle = styled.div`
    font-size: var(--font-large);
    color: #696969;
`;

const LabelsContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    align-items: center;
`;

const Balloon = styled.div`
    font-size: var(--font-medium);
    font-weight: var(--font-bold);
    margin: 0 30px;
    background-color: #fffff9;
    padding: 15px;
    border-radius: 10px;
    position: relative;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.06);
    line-height: 1.5;
    white-space: pre-wrap;

    &::before {
        content: '';
        position: absolute;
        top: -12px;
        left: 20px;
        width: 0;
        height: 0;
        border-left: 12px solid transparent;
        border-right: 12px solid transparent;
        border-bottom: 12px solid #fffff9;
    }
`;

const getLabel = (choices: { value: string; label: string }[] | string[][], value: string) => {
    if (!value) return '';
    const choice = Array.isArray(choices[0])
        ? (choices as string[][]).find(([v]) => v === value)
        : (choices as { value: string; label: string }[]).find((v) => v.value === value);
    return choice ? ('label' in choice ? choice.label : choice[1]) : value;
};

export const Preview: React.FC<{
    agent: any;
    editing: boolean;
    setEditing?: (editing: boolean) => void;
    areaChoices: { value: string; label: string }[];
    employeeChoices: string[][];
    industryChoices: { value: string; label: string }[];
}> = ({ agent, editing, setEditing, areaChoices, industryChoices }) => {
    return (
        <Wrapper>
            <SubtitleContainer>
                {!editing && setEditing && (
                    <EditButton onClick={() => setEditing(true)}>
                        <img src={Pencil} alt="Edit" />
                        修正する
                    </EditButton>
                )}
            </SubtitleContainer>
            <ContentWrapper>
                <HeaderSection>
                    {agent.icon_image && (
                        <CompanyImageWrapper>
                            <CompanyImage src={agent.icon_image} alt={agent.name} />
                        </CompanyImageWrapper>
                    )}
                    <CompanyInfo>
                        <CompanyName>
                            {agent.name}
                            <Subtitle>（プレビュー）</Subtitle>
                        </CompanyName>
                        {agent?.company_code && <CompanyLocation>{agent.company_code}</CompanyLocation>}
                        <LabelsContainer>
                            {agent.agent_type === AGENT_TYPES.AGENCY.value && (
                                <ColorLabel color="#ee7800" backgroundColor="#fafad2" borderColor="#ee7800">
                                    {AGENT_TYPES.AGENCY.label}
                                </ColorLabel>
                            )}
                            {agent.agent_type === AGENT_TYPES.FA.value && (
                                <ColorLabel color="#006eb0" backgroundColor="#e5ffff" borderColor="#006eb0">
                                    {AGENT_TYPES.FA.label}
                                </ColorLabel>
                            )}
                            {agent.agent_type === AGENT_TYPES.BUSINESS_ACQUISITION.value && (
                                <ColorLabel color="#56256e" backgroundColor="#efefff" borderColor="#56256e">
                                    {AGENT_TYPES.BUSINESS_ACQUISITION.label}
                                </ColorLabel>
                            )}
                            {agent.is_sole_proprietor && (
                                <ColorLabel color="#008000" backgroundColor="#d3ffa8" borderColor="#008000">
                                    個人事業主
                                </ColorLabel>
                            )}
                        </LabelsContainer>
                    </CompanyInfo>
                </HeaderSection>

                {agent.business_description && <Balloon>{agent.business_description}</Balloon>}

                <ContentSection>
                    <SubTitleLabel>この企業について</SubTitleLabel>
                    <Item>
                        <Title>対応可能な業種</Title>
                        <IndustryLabelContainer>
                            {Array.isArray(agent.industries_served) &&
                                agent.industries_served
                                    .filter((industry: string) => industry && industry.trim() !== '')
                                    .map((industry: string) => (
                                        <GrayLabel key={industry}>{getLabel(industryChoices, industry)}</GrayLabel>
                                    ))}
                        </IndustryLabelContainer>
                    </Item>

                    {agent.regions_served && (
                        <Item>
                            <Title>対応可能なエリア</Title>
                            <AreaLabelContainer>
                                {Array.isArray(agent.regions_served) &&
                                    agent.regions_served
                                        .filter((area: string) => area && area.trim() !== '')
                                        .map((area: string) => (
                                            <GrayLabel key={area}>{getLabel(areaChoices, area)}</GrayLabel>
                                        ))}
                            </AreaLabelContainer>
                        </Item>
                    )}
                </ContentSection>
            </ContentWrapper>
        </Wrapper>
    );
};
